import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaSearch, 
  FaFilter, 
  FaUserShield,
  FaCheckCircle,
  FaEdit,
  FaTrash,
  FaBan,
  FaDollarSign,
  FaFileAlt,
  FaDownload
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import Pagination from '../Common/Pagination';
import './AuditLog.css';

const JsonDiffViewer = ({ data, compareData }) => {
  const isDiffMode = compareData !== undefined;

  const renderValue = (val, compVal, isDeletedNode = false) => {
    if (val === undefined) return null;
    if (val === null) return <span className="jv-null">null</span>;
    if (typeof val === 'boolean') return <span className="jv-boolean">{val ? 'true' : 'false'}</span>;
    if (typeof val === 'number') return <span className="jv-number">{val}</span>;
    if (typeof val === 'string') return <span className="jv-string">"{val}"</span>;

    if (Array.isArray(val)) {
      if (val.length === 0) return <span>[]</span>;
      const maxLength = isDiffMode && Array.isArray(compVal) ? Math.max(val.length, compVal.length) : val.length;
      
      return (
        <span>
          [
          <div className="jv-indent">
            {Array.from({ length: maxLength }).map((_, index) => {
              const item = val[index];
              const cItem = isDiffMode && Array.isArray(compVal) ? compVal[index] : undefined;
              
              const isDeleted = isDiffMode && item === undefined && cItem !== undefined;
              if (isDeleted) {
                return (
                  <div key={index} className="jv-diff-deleted">
                    {renderValue(cItem, undefined, true)}
                    {index < maxLength - 1 ? ',' : ''}
                  </div>
                );
              }

              const isDiff = isDiffMode && !isDeletedNode && JSON.stringify(item) !== JSON.stringify(cItem);
              const isPrim = item === null || typeof item !== 'object';
              
              return (
                <div key={index} className={isDiff && isPrim ? 'jv-diff-highlight' : ''}>
                  {renderValue(item, cItem, isDeletedNode)}
                  {index < maxLength - 1 ? ',' : ''}
                </div>
              );
            })}
          </div>
          ]
        </span>
      );
    }

    if (typeof val === 'object') {
      const keys = Object.keys(val);
      let allKeys = keys;
      if (isDiffMode && compVal && typeof compVal === 'object') {
        allKeys = Array.from(new Set([...keys, ...Object.keys(compVal)]));
      }

      if (allKeys.length === 0) return <span>{'{ }'}</span>;
      return (
        <span>
          {'{'}
          <div className="jv-indent">
            {allKeys.map((key, index) => {
              const item = val[key];
              const cItem = isDiffMode && compVal && typeof compVal === 'object' ? compVal[key] : undefined;
              
              const isDeleted = isDiffMode && item === undefined && cItem !== undefined;
              if (isDeleted) {
                return (
                  <div key={key} className="jv-diff-deleted">
                    <span className="jv-key">"{key}"</span>: {renderValue(cItem, undefined, true)}
                    {index < allKeys.length - 1 ? ',' : ''}
                  </div>
                );
              }

              const isDiff = isDiffMode && !isDeletedNode && JSON.stringify(item) !== JSON.stringify(cItem);
              const isPrim = item === null || typeof item !== 'object';
              
              return (
                <div key={key} className={isDiff && isPrim ? 'jv-diff-highlight' : ''}>
                  <span className={isDiff && !isPrim ? "jv-key jv-diff-highlight" : "jv-key"}>"{key}"</span>: {renderValue(item, cItem, isDeletedNode)}
                  {index < allKeys.length - 1 ? ',' : ''}
                </div>
              );
            })}
          </div>
          {'}'}
        </span>
      );
    }
    
    return <span>{String(val)}</span>;
  };

  return <div className="json-diff-viewer">{renderValue(data, compareData)}</div>;
};

const AuditLog = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    approves: 0,
    updates: 0,
    rejects: 0
  });
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    total: 0
  });

  const getActionConfig = (action) => {
    const act = (action || '').toLowerCase();
    if (['approve', 'release', 'create', 'resolve', 'completed'].includes(act)) return { icon: <FaCheckCircle />, color: 'success' };
    if (['reject', 'suspend', 'delete', 'cancelled'].includes(act)) return { icon: <FaBan />, color: 'danger' };
    if (['refund', 'flagged'].includes(act)) return { icon: <FaDollarSign />, color: 'warning' };
    return { icon: <FaEdit />, color: 'info' };
  };

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      
      let startDate, endDate;
      const today = new Date();
      if (dateFilter !== 'all') {
        endDate = today.toISOString();
        const start = new Date();
        if (dateFilter === 'today') start.setHours(0,0,0,0);
        else if (dateFilter === '7days') start.setDate(today.getDate() - 7);
        else if (dateFilter === '30days') start.setDate(today.getDate() - 30);
        else if (dateFilter === '90days') start.setDate(today.getDate() - 90);
        startDate = start.toISOString();
      }

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        action: actionFilter === 'all' ? undefined : actionFilter.toLowerCase(),
        entityType: entityFilter === 'all' ? undefined : entityFilter.toLowerCase(),
        search: searchTerm || undefined,
        startDate,
        endDate
      };

      const [logsRes, statsRes] = await Promise.all([
        api.get(ENDPOINTS.AUDIT.LOGS, { params }),
        api.get(ENDPOINTS.AUDIT.STATS)
      ]);

      const data = logsRes.data?.data || logsRes.data || logsRes;
      const logs = Array.isArray(data) ? data : [];
      
      setAuditLogs(logs);
      setStats(statsRes.data || statsRes);
      setPagination(prev => ({ 
        ...prev, 
        total: logsRes.data?.meta?.total || logsRes.meta?.total || logsRes.total || logs.length 
      }));
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
      setIsFirstLoad(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(inputValue), 500);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    fetchAuditLogs();
  }, [pagination.page, actionFilter, entityFilter, dateFilter, searchTerm]);

  if (loading && isFirstLoad) {
    return (
      <div className="loading-spinner-container">
        <div className="modern-spinner"></div>
        <p className="loading-text">Fetching live audit logs...</p>
      </div>
    );
  }

  const handleExport = () => {
    toast.info('Exporting audit logs to CSV...');
  };

  return (
    <div className="audit-log-page">
      <div className="page-header">
        <div>
          <h1>Audit Log</h1>
          <p className="page-subtitle">Track all admin actions and system changes</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={handleExport}>
            <FaDownload /> Export Logs
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="audit-stats">
        <div className="stat-mini-card">
          <div className="stat-mini-icon">
            <FaFileAlt />
          </div>
          <div className="stat-mini-content">
            <h3>{stats.total || 0}</h3>
            <p>Total Actions</p>
          </div>
        </div>
        <div className="stat-mini-card">
          <div className="stat-mini-icon success">
            <FaCheckCircle />
          </div>
          <div className="stat-mini-content">
            <h3>{stats.approves || 0}</h3>
            <p>Approvals</p>
          </div>
        </div>
        <div className="stat-mini-card">
          <div className="stat-mini-icon info">
            <FaEdit />
          </div>
          <div className="stat-mini-content">
            <h3>{stats.updates || 0}</h3>
            <p>Updates</p>
          </div>
        </div>
        <div className="stat-mini-card">
          <div className="stat-mini-icon danger">
            <FaBan />
          </div>
          <div className="stat-mini-content">
            <h3>{stats.rejects || 0}</h3>
            <p>Rejections</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="content-card filters-card">
        <div className="filters-row">
          <div className="search-box" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by admin or entity ID..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              className="search-input"
              style={{ paddingRight: '30px' }}
            />
            {inputValue && (
              <button 
                onClick={() => setInputValue('')}
                style={{ position: 'absolute', right: '12px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0', fontSize: '16px', display: 'flex', alignItems: 'center' }}
                title="Clear search"
              >
                &#x2715;
              </button>
            )}
          </div>

          <div className="filter-group">
            <FaFilter className="filter-icon" />
            <select 
              value={actionFilter} 
              onChange={(e) => setActionFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Actions</option>
              <option value="approve">Approved</option>
              <option value="reject">Rejected</option>
              <option value="update">Updated</option>
              <option value="create">Created</option>
              <option value="delete">Deleted</option>
              <option value="release">Released</option>
              <option value="refund">Refunded</option>
              <option value="suspend">Suspended</option>
            </select>
          </div>

          <div className="filter-group">
            <select 
              value={entityFilter} 
              onChange={(e) => setEntityFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Entities</option>
              <option value="provider">Providers</option>
              <option value="customer">Customers</option>
              <option value="booking">Bookings</option>
              {/* <option value="payment">Payments</option> */}
              <option value="payout">Payouts</option>
              {/* <option value="dispute">Disputes</option>
              <option value="promotion">Promotions</option> */}
              <option value="service">Services</option>
              {/* <option value="setting">Settings</option> */}
            </select>
          </div>

          <div className="filter-group">
            <select 
              value={dateFilter} 
              onChange={(e) => setDateFilter(e.target.value)}
              className="filter-select"
            >
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>
        </div>
        <div className="filters-summary" style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13.5px', color: '#636e72', borderTop: '1px solid #f1f2f6', paddingTop: '10px' }}>
          <div>
            Active Filters: <span style={{ textTransform: 'capitalize', fontWeight: '600' }}>{actionFilter === 'all' ? 'All Actions' : actionFilter}</span> • <span style={{ textTransform: 'capitalize', fontWeight: '600' }}>{entityFilter === 'all' ? 'All Entities' : entityFilter}</span> • <span style={{ textTransform: 'capitalize', fontWeight: '600' }}>{dateFilter === 'all' ? 'All Time' : dateFilter}</span>
          </div>
          <div>
            Found <strong>{pagination.total}</strong> matching logs
          </div>
        </div>
      </div>

      {/* Audit Log Timeline */}
      <div className="content-card" style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {auditLogs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <h3>No audit logs found</h3>
            <p>Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <div className="audit-timeline">
            {auditLogs.map((log) => {
              const { icon, color } = getActionConfig(log.action);
              return (
                <div key={log.id} className="audit-entry">
                  <div className={`audit-icon ${color}`}>
                    {icon}
                  </div>
                  <div className="audit-content">
                    <div className="audit-header">
                      <div className="audit-title">
                        <h4>{log.entityName || `${log.entityType} Activity`}</h4>
                        <span className={`badge badge-${color}`}>{log.action}</span>
                      </div>
                      <div className="audit-time">{new Date(log.timestamp || log.createdAt).toLocaleString()}</div>
                    </div>
                    
                    <div className="audit-details">
                      <div className="detail-row">
                        <FaUserShield className="detail-icon" />
                        <span>
                          <strong>{log.adminName || (log.admin ? `${log.admin.firstName} ${log.admin.lastName}` : 'System')}</strong> {(log.adminEmail || log.admin?.email) && `(${log.adminEmail || log.admin?.email})`}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Entity:</span>
                        <span className="entity-badge">{log.entityType}</span>
                        <span className="detail-label">ID:</span>
                        <code className="entity-id">{log.entityId}</code>
                      </div>
                    </div>

                    <div className="audit-changes">
                      {log.oldValue && Object.keys(log.oldValue).length > 0 && (
                        <div className="change-block">
                          <label>Previous State:</label>
                          <div className="json-preview">
                            <JsonDiffViewer data={log.oldValue} />
                          </div>
                        </div>
                      )}
                      {log.newValue && Object.keys(log.newValue).length > 0 && (
                        <div className="change-block">
                          <label>Changes:</label>
                          <div className="json-preview">
                            <JsonDiffViewer data={log.newValue} compareData={log.oldValue} />
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="audit-meta">
                      {log.ipAddress && <span className="meta-item"><strong>IP:</strong> {log.ipAddress}</span>}
                      {log.userAgent && <span className="meta-item"><strong>UA:</strong> {log.userAgent.substring(0, 40)}...</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Pagination 
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        />
      </div>
    </div>
  );
};

export default AuditLog;